<template>
  <div id="editor" class="pages-container">
    <div class="row">
      <div class="col">
        <button type="button" @click="addPage" class="btn TopPageBT TopAdd btn-sm">Add Page</button>
      </div>
      <div class="col" style="display: flex; justify-content: flex-end; align-items: center; margin-top:-20px">
        <div class="icon-container" style="margin-right: 10px;">
          <img title="Import Question from item collection" @click="$emit('toggle-item-bank')"
            src="@/assets/Import2ItemLib.png" alt="import items" />
        </div>
        <div hidden class="icon-container">
          <img title="Export All Questions to the item collection (except duplicates)"
            @click="console.log('DISPLAY ITEM BANK WITH SELECTOR SYSTEM')" src="@/assets/Export2ItemLib.png"
            alt="export items" />
        </div>
      </div>
    </div>

    <!-- 🔽 Container draggable pour toutes les pages -->
    <draggable v-model="pagesArray" group="pages" handle=".page-drag-handle" @end="handlePagesReorder"
      @start="handlePageDragStart" @move="handlePageDragMove" @cancel="handlePageDragCancel" item-key="id"
      class="pages-draggable-container" ghost-class="page-ghost" drag-class="page-drag" chosen-class="page-chosen">
      <template #item="{ element: page, index }">
        <div class="mb-4" :class="{
          'active-page': isPageActive(page),
          'collapsed-page': isPageCollapsed(page.id),
          'page-drag-over': isDragOver === page.id
        }">
          <div class="d-flex flex-column gap-2 pageGroup" :class="{ 'collapsed': isPageCollapsed(page.id) }"
            :data-page-id="page.id">
            <div class="row page-header">
              <div class="col">
                <div class="header-content">
                  <!-- Groupe de gauche : 2 éléments -->
                  <div class="left-group">
                    <div class="page-drag-handle" title="Drag to reorder pages">
                      <i class="fas fa-grip-vertical"></i>
                    </div>

                    <button class="collapse-caret" @click="togglePageCollapse(page.id)"
                      :class="{ 'collapsed': isPageCollapsed(page.id) }" title="Replier/Déplier la page">
                      <span class="caret-icon">▼</span>
                    </button>
                  </div>

                  <!-- Groupe du centre : 2 éléments -->
                  <div class="center-group">
                    <h3 class="page-title" style="color:dimgrey">Page {{ index + 1 }}</h3>
                    <button class="pageParameter" @click="openPageParameterModal(page)" title="Page Parameters">
                      <font-awesome-icon :icon="['fas', 'cog']" />
                    </button>
                  </div>

                  <!-- Groupe de droite : 2 éléments -->
                  <div class="right-group">
                    <button class="action-btn addEl" @click="addElement(page.id)" title="Add Elements">
                      <font-awesome-icon :icon="['fas', 'plus-circle']" />
                    </button>
                    <button class="action-btn trashcan" @click.stop="deletePage(page.id)" title="Delete Page">
                      <font-awesome-icon :icon="['fas', 'trash']" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 🔽 Zone de contenu avec effect de collapse + zone de drop universelle -->
            <div class="page-content" :class="{ 'collapsed': isPageCollapsed(page.id) }"
              @dragover.prevent="handleDragOver(page.id, $event)" @dragleave="handleDragLeave($event)"
              @drop="(e) => onDrop(page, e)">

              <!-- 🔽 Aperçu empilé quand la page est repliée -->
              <div v-if="isPageCollapsed(page.id)" class="collapsed-content">
                <!-- Aperçu des questions empilées (si il y en a) -->
                <div v-if="page.children.length > 0" class="stacked-preview">
                  <div v-for="(child, childIndex) in page.children.slice(0, 3)" :key="child.id" class="stacked-card"
                    :style="{
                      'z-index': 3 - childIndex,
                      'transform': `translateY(${childIndex * 3}px) translateX(${childIndex * 2}px)`,
                      'opacity': 1 - (childIndex * 0.2)
                    }">
                    <div class="card-mini">
                      <div class="interaction-type-mini">{{ getInteractionType(child.id) }}</div>
                      <div class="card-title-mini">{{ child.label }}</div>
                    </div>
                  </div>
                  <div class="more-items">
                    ({{ page.children.length }})
                  </div>
                </div>

                <!-- Message si pas de questions -->
                <div v-else class="empty-page-message">
                  <i class="fas fa-inbox" style="color: #ccc; font-size: 1.5rem; margin-bottom: 5px;"></i>
                  <span style="color: #666; font-size: 0.9rem;">No questions yet</span>
                </div>

                <!-- 🔽 Zone de drop TOUJOURS visible pour pages repliées -->
                <div class="collapsed-drop-zone" :class="{ 'drag-over': isDragOver === page.id }">
                  <div class="drop-indicator">
                    <font-awesome-icon :icon="['fas', 'plus']" />
                    <span>Drop questions or import items here</span>
                  </div>
                </div>
              </div>

              <!-- 🔽 Zone draggable normale quand la page est ouverte -->
           <draggable
              v-else
              v-model="page.children"
              group="elements"
              item-key="id"
              class="drag-container"
              handle=".drag-handle"
              :delay="120"
              :delayOnTouchOnly="true"
              :touchStartThreshold="8"
              @start="onElementDragStart"
              @end="onElementDragEnd"
              @add="handleDragAdd"
              :clone="cloneElement"
            >

                <template #item="{ element: child }">
                  <div class="card cursor-pointer" :class="{ 'active-card': child.id === activeElementId }">
                    <!-- Poignée de drag fine sur toute la largeur -->
                    <div class="drag-handle" title="Drag to reorder element">
                      <div class="drag-indicator"></div>
                    </div>
                    
                    <!-- Zone cliquable pour ouvrir l'item -->
                    <div class="card-content"
                      @pointerdown="handlePointerDown(child.id, child.label, $event)"
                      @pointerup="handlePointerUp(child.id, child.label, $event)"
                      @pointermove="handlePointerMove($event)"
                      role="button" tabindex="0" @keydown.enter="handleElementClick(child.id, child.label)"
                      @keydown.space.prevent="handleElementClick(child.id, child.label)">
                      <div class="card-body row">
                        <div>
                        <div class="row">
                          <div class="col">
                            <div class="interaction-type">{{ getInteractionType(child.id) }}</div>
                          </div>
                          <div class="col position-relative" style="text-align: right; margin-top: -15px!important">
                            <i class="fa-solid fa-pen-to-square editPageLabel" @click.stop="enableEdit(child)"></i>
                            <span title="Delete Element!" class="delElement" @click.stop="deleteElement(child.id)">
                              <i class="fa-solid fa-xmark"></i>
                            </span>
                          </div>
                        </div>

                        <h5 v-if="!child.isEditing" class="card-title" @dblclick="enableEdit(child)">
                          {{ child.label }}
                        </h5>
                        <input v-else type="text" class="card-title-input" v-model="child.label"
                          @blur="disableEdit(child)" @keyup.enter="disableEdit(child)"
                          :ref="(el) => editInput[child.id] = el" />
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </draggable>
            </div>
          </div>
        </div>
      </template>
    </draggable>

    <div class="row">
      <div class="col">
        <button type="button" @click="addPage" class="btn TopPageBT TopAdd btn-sm">Add Page</button>
      </div>
      <div class="col" style="display: flex; justify-content: flex-end; align-items: center; margin-top:-20px">
        <div class="icon-container" style="margin-right: 10px;">
          <img title="Import Question from item collection" @click="$emit('toggle-item-bank')"
            src="@/assets/Import2ItemLib.png" alt="import items" />
        </div>
        <div hidden class="icon-container">
          <img title="Export All Questions to the item collection (except duplicates)"
            @click="console.log('DISPLAY ITEM BANK WITH SELECTOR SYSTEM')" src="@/assets/Export2ItemLib.png"
            alt="export items" />
        </div>
      </div>
    </div>


  </div>

  <teleport to="body">
    <div v-if="editingElement" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);"
      @click.self="cancelEdit">
      <div class="modal-dialog modal-dialog-centered" @click.stop>
        <div class="modal-content shadow">
          <div class="modal-header">
            <h5 class="modal-title">Modifier le label</h5>
            <button type="button" class="btn-close" @click="cancelEdit"></button>
          </div>
          <div class="modal-body">
            <input v-model="editLabel" @keyup.enter="confirmEdit" type="text" class="form-control"
              placeholder="Nouveau label" autofocus />
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="cancelEdit">Annuler</button>
            <button type="button" class="btn btn-primary" @click="confirmEdit">Valider</button>
          </div>
        </div>
      </div>
    </div>
  </teleport>

  <!-- 🎛️ Modal des paramètres de page (composant séparé) -->
  <PageParameterModal :visible="showPageParameterModal" :pageData="currentPageForParameters"
    @close="closePageParameterModal" @apply="handlePageParametersApply" />

</template>


<script setup>
import { useTestStore } from '../stores/testStore'
import { computed, ref, nextTick } from 'vue'
import draggable from 'vuedraggable'
import { v4 as uuidv4 } from 'uuid'
import api from "@/services/axios";
import dimCircle from "@/components/DimensionCircleSelect.vue";
import PageParameterModal from "./PageParameterModal.vue";

const store = useTestStore()
const editInput = ref({});

const editingElement = ref(null);
const editLabel = ref("");

// 🎛️ Modal de paramètres de page
const showPageParameterModal = ref(false);
const currentPageForParameters = ref(null);

// 🔽 État des pages repliées/dépliées (par défaut toutes ouvertes)
const collapsedPages = ref(new Set());

// 🎯 État de drag over pour feedback visuel
const isDragOver = ref(null);

// 🔄 État pour savoir si on est en train de draguer une page
const isDraggingPage = ref(false);

// 🔽 Fonction pour basculer l'état d'une page
const togglePageCollapse = (pageId) => {
  if (collapsedPages.value.has(pageId)) {
    collapsedPages.value.delete(pageId);
  } else {
    collapsedPages.value.add(pageId);
  }
  // Force la réactivité
  collapsedPages.value = new Set(collapsedPages.value);
};

const isDraggingElement = ref(false);
let dragJustEndedAt = 0;

// Variables pour la détection de tap vs drag
let pointerStartPos = { x: 0, y: 0 };
let pointerStartTime = 0;
let isPointerDown = false;
let hasMoved = false;

const onElementDragStart = () => { 
  isDraggingElement.value = true;
  hasMoved = true; // Un drag a commencé, donc on a bougé
};
const onElementDragEnd = (event) => {
  isDraggingElement.value = false;
  dragJustEndedAt = Date.now();
  
  // 🔄 Sauvegarder les changements quand un élément est réordonné dans la même page
  if (event && event.from) {
    const pageElement = event.from.closest('.pageGroup');
    if (pageElement) {
      const pageId = pageElement.dataset.pageId;
      const page = pages.value.find(p => p.id === pageId);
      if (page) {
        console.log(`🔄 Ordre des éléments mis à jour pour la page ${pageId}`);
        store.updatePageOrder(pageId, page.children);
        store.saveTestDataToDatabase();
        console.log(`✅ Sauvegarde effectuée pour la page ${pageId}`);
      }
    }
  }
  
  // Reset après un délai
  setTimeout(() => {
    hasMoved = false;
  }, 100);
};

// Nouvelle approche unifiée pour gérer tap/click
const handlePointerDown = (id, label, event) => {
  // Ignorer complètement si on clique sur le drag handle ou ses enfants
  if (event.target.closest('.drag-handle')) {
    return;
  }
  
  isPointerDown = true;
  hasMoved = false;
  pointerStartPos = { x: event.clientX, y: event.clientY };
  pointerStartTime = Date.now();
};

const handlePointerMove = (event) => {
  if (!isPointerDown) return;
  
  const deltaX = Math.abs(event.clientX - pointerStartPos.x);
  const deltaY = Math.abs(event.clientY - pointerStartPos.y);
  
  // Si on bouge de plus de 10px, c'est un drag potentiel
  if (deltaX > 10 || deltaY > 10) {
    hasMoved = true;
  }
};

const handlePointerUp = (id, label, event) => {
  if (!isPointerDown) return;
  
  isPointerDown = false;
  
  // Ignorer si on relâche sur le drag handle
  if (event.target.closest('.drag-handle')) {
    return;
  }
  
  // Éviter le "tap fantôme" juste après un drag
  if (isDraggingElement.value || Date.now() - dragJustEndedAt < 200) {
    return;
  }
  
  const timeDelta = Date.now() - pointerStartTime;
  const deltaX = Math.abs(event.clientX - pointerStartPos.x);
  const deltaY = Math.abs(event.clientY - pointerStartPos.y);
  
  // C'est un tap si :
  // - On n'a pas bougé de plus de 8px
  // - Le temps est inférieur à 500ms
  // - On n'est pas en train de draguer
  if (!hasMoved && deltaX <= 8 && deltaY <= 8 && timeDelta < 500) {
    handleElementClick(id, label);
  }
};

// 🔽 Vérifier si une page est repliée
const isPageCollapsed = (pageId) => {
  return collapsedPages.value.has(pageId);
};

// 🎛️ Ouvrir la modal des paramètres de page
const openPageParameterModal = (page) => {
  currentPageForParameters.value = page;
  showPageParameterModal.value = true;
  console.log("🎛️ Ouverture modal paramètres pour page:", page.label);
};

// 🎛️ Fermer la modal des paramètres de page
const closePageParameterModal = () => {
  showPageParameterModal.value = false;
  currentPageForParameters.value = null;
};

// 🎛️ Gérer l'application des paramètres de page
const handlePageParametersApply = (parameterData) => {
  console.log("🎛️ Application des paramètres de page:", parameterData);
  console.log("🎛️ Portée d'application:", parameterData.scope);

  // TODO: Intégrer avec le store pour sauvegarder les paramètres
  switch (parameterData.scope) {
    case 'current':
      console.log("📄 Application à la page actuelle uniquement");
      // store.updatePageParameters(parameterData.pageId, parameterData.parameters);
      break;

    case 'next':
      console.log("➡️ Application aux pages suivantes");
      // TODO: Trouver toutes les pages après la page actuelle et appliquer
      // const currentPageIndex = store.getPageIndex(parameterData.pageId);
      // store.updateMultiplePageParameters(currentPageIndex + 1, parameterData.parameters);
      break;

    case 'all':
      console.log("🌐 Application à toutes les pages du test");
      // TODO: Appliquer à toutes les pages
      // store.updateAllPageParameters(parameterData.parameters);
      break;

    default:
      console.warn("⚠️ Portée d'application inconnue:", parameterData.scope);
  }

  // Afficher une notification de succès (optionnel)
  console.log("✅ Paramètres de page appliqués avec succès");
};

// 🎯 Gestion des événements drag over/leave
const handleDragOver = (pageId, event) => {
  // Ignorer si on drag une page (pas un élément)
  if (isDraggingPage.value) return;

  event.preventDefault();
  isDragOver.value = pageId;
};

const handleDragLeave = (event) => {
  // Ignorer si on drag une page (pas un élément)
  if (isDraggingPage.value) return;

  // Vérifier si on quitte vraiment la zone (pas juste entre enfants)
  if (!event.currentTarget.contains(event.relatedTarget)) {
    isDragOver.value = null;
  }
};

const emit = defineEmits(['clear-preview-and-editor', "element-clicked", 'toggle-item-bank'])

const pages = computed(() => store.testData?.pages || [])

// 🔽 Propriété computed réactive pour Vue Draggable des pages
const pagesArray = computed({
  get: () => store.testData?.pages || [],
  set: (newPages) => {
    // Cette méthode sera appelée automatiquement par Vue Draggable
    console.log("🔄 Nouvelles pages reçues via setter:", newPages.map(p => p.label));
    // Mettre à jour directement le store
    store.updatePagesOrder(newPages);
  }
})
const activeElementId = ref(null) // Variable réactive pour l'élément actif

// Vérifie si une page contient l'élément actif
const isPageActive = (page) => {
  return page.children.some((child) => child.id === activeElementId.value)
}

const onDragStart = (event) => {
  const draggedElement = event.item?.__draggable_context?.element
  console.log("🚀 Drag start:", draggedElement?.id)
  if (draggedElement && draggedElement.id) {
    // S'assurer que l'événement original existe
    if (event.originalEvent && event.originalEvent.dataTransfer) {
      event.originalEvent.dataTransfer.setData("text/plain", draggedElement.id)
      event.originalEvent.dataTransfer.effectAllowed = "move"
      console.log("✅ Drag start ID enregistré :", draggedElement.id)
    } else {
      console.warn("❌ Pas d'originalEvent.dataTransfer disponible")
    }
  } else {
    console.warn("❌ Pas de dataTransfer.setData car id manquant :", draggedElement)
  }
}

const cloneElement = (original) => {
  return {
    ...original,
    isEditing: false // ✅ toujours défini
  };
};

const handleElementClick = (elementId, label) => {
  if (elementId) {
    activeElementId.value = elementId
    const element = store.getElementById(elementId)

    if (element) {
      store.setSelectedElement(element, label)
      const rect = document.querySelector(`.card.cursor-pointer.active-card`)?.getBoundingClientRect();
      const position = rect
        ? { top: rect.top, height: rect.height }
        : { top: 0, height: 0 };
      // Émettre les données de position
      emit("element-clicked", position);
    }
  }
}

//Récupère le type d'interaction : 
const getInteractionType = (elementId) => {
  const element = store.testData.elements.find(el => el.el_ID === elementId);
  return element ? element.el_Type : 'Type non défini';
};

// Ajout d'une nouvelle page
const addPage = () => {
  const newPage = {
    id: uuidv4(),
    label: `Page ${pages.value.length + 1}`,
    children: [],
  }
  store.addPage(newPage)
}

// Supprimer une page
const deletePage = (pageId) => {
  // Confirmer la suppression avec l'utilisateur
  if (confirm("Êtes-vous sûr de vouloir supprimer cette page ?")) {
    // Trouver la page à supprimer
    const pageIndex = store.testData.pages.findIndex(page => page.id === pageId);
    if (pageIndex === -1) return; // Si la page n'existe pas, on arrête la fonction

    // Récupérer les IDs des éléments associés à cette page
    const elementsToDelete = store.testData.pages[pageIndex].children.map(child => child.id);

    // Supprimer les éléments associés dans testData.elements
    store.testData.elements = store.testData.elements.filter(el => !elementsToDelete.includes(el.el_ID));

    // Supprimer la page de testData.pages
    store.testData.pages.splice(pageIndex, 1);

    console.log(`Page ${pageId} et ses éléments associés ont été supprimés.`);
    store.saveTestDataToDatabase();
  }
};

const addElement = (pageID) => {
  const ElementID = uuidv4()
  store.addElement(pageID, ElementID)

  // Sélectionner automatiquement le nouvel élément pour ouvrir l'éditeur
  const newElement = store.getElementById(ElementID)
  if (newElement) {
    store.setSelectedElement(newElement, "New Element")
    activeElementId.value = ElementID

    // Émettre l'événement pour mettre à jour la position de l'éditeur
    emit("element-clicked", { top: 0, height: 0 })
  }
}

const deleteElement = (childID) => {
  // Récupérer le label de l'élément pour un message plus informatif
  const element = store.getElementById(childID);
  const elementLabel = element?.el_Label || element?.el_Text?.substring(0, 50) || "Cet élément";

  if (confirm(`Êtes-vous sûr de vouloir supprimer "${elementLabel}" ?\n\nCette action est irréversible.`)) {
    const success = store.deleteElement(childID);

    if (success) {
      // Émettre un événement pour nettoyer le preview et l'éditeur
      emit('clear-preview-and-editor');
      console.log(`✅ Element "${elementLabel}" supprimé avec succès`);
    } else {
      alert("Erreur lors de la suppression de l'élément.");
      console.error(`❌ Échec de la suppression de l'élément ${childID}`);
    }
  }
};




// Ouvrir la modale
const enableEdit = (child) => {
  editingElement.value = child;
  editLabel.value = child.label;
};

// Valider
const confirmEdit = () => {
  if (!editingElement.value) return;

  const fullElement = store.testData.elements.find(
    el => el.el_ID === editingElement.value.id
  );

  if (fullElement) {
    fullElement.el_Label = editLabel.value;
    editingElement.value.label = editLabel.value;
    store.updateElement(fullElement);
  } else {
    // Nouveau ou non encore intégré : ajout ?
    console.warn("⚠️ Élément non trouvé dans testData.elements :", editingElement.value.id);
  }

  editingElement.value = null;
};

// Annuler
const cancelEdit = () => {
  editingElement.value = null;
};



// 🔄 Fonction de drop universelle (import item bank + drag interne)
const onDrop = async (page, event) => {
  try {
    // Réinitialiser l'état de drag over
    isDragOver.value = null;

    const rawId = event.dataTransfer.getData("text/plain");

    console.log("🎯 Drop détecté avec ID:", rawId, "vers page:", page.id);

    // 🔒 Vérification ObjectId (import depuis item bank)
    if (/^[0-9a-fA-F]{24}$/.test(rawId)) {
      console.log("📦 Import depuis item bank détecté");
      await handleItemBankDrop(page, rawId);
      return;
    }

    // 🔄 Drag interne entre pages (UUID)
    if (rawId && rawId.includes('-')) {
      console.log("🔄 Drag interne détecté");
      await handleInternalDrop(page, rawId);
      return;
    }

    console.warn("⛔ ID non reconnu:", rawId);
  } catch (error) {
    console.error("❌ Erreur lors du drop :", error);
    // Réinitialiser même en cas d'erreur
    isDragOver.value = null;
  }
};

// 📦 Gestion du drop depuis item bank
const handleItemBankDrop = async (page, objectId) => {
  try {
    const encodedId = encodeURIComponent(objectId);
    const response = await api.get(`/items/full/${encodedId}`);
    const fullItem = response.data;

    if (!fullItem || !fullItem._id) {
      console.error("⚠️ Élément introuvable ou incomplet :", fullItem);
      return;
    }

    const newElementID = uuidv4();
    const newElement = {
      ...fullItem,
      el_ID: newElementID,
      _id: undefined,
      isNewElement: true,
    };

    store.addElementFromImport(newElement);
    page.children.push({ id: newElementID, label: newElement.el_Label, isEditing: false });
    store.saveTestDataToDatabase();

    console.log(`✅ Élément importé vers la page ${page.label}`, newElement);
  } catch (error) {
    console.error("❌ Erreur lors de l'import depuis item bank :", error);
  }
};

// 🔄 Gestion du drag interne entre pages
const handleInternalDrop = async (targetPage, draggedElementId) => {
  try {
    // Trouver l'élément dans les pages
    let sourcePageId = null;
    let draggedElement = null;

    for (const page of pages.value) {
      const elementIndex = page.children.findIndex(child => child.id === draggedElementId);
      if (elementIndex !== -1) {
        draggedElement = page.children[elementIndex];
        sourcePageId = page.id;
        // Retirer l'élément de la page source
        page.children.splice(elementIndex, 1);
        break;
      }
    }

    if (!draggedElement) {
      console.warn("❌ Élément dragué non trouvé:", draggedElementId);
      return;
    }

    // Éviter le drop sur la même page
    if (sourcePageId === targetPage.id) {
      console.log("🔄 Drop sur la même page, annulation");
      return;
    }

    // Ajouter l'élément à la page cible
    targetPage.children.push(draggedElement);

    // Mettre à jour l'ordre des pages dans le store
    store.updatePageOrder(targetPage.id, targetPage.children);
    if (sourcePageId) {
      const sourcePage = pages.value.find(p => p.id === sourcePageId);
      if (sourcePage) {
        store.updatePageOrder(sourcePageId, sourcePage.children);
      }
    }
    
    // 🔄 Sauvegarder les changements en base de données
    store.saveTestDataToDatabase();

    console.log(`✅ Élément déplacé de page ${sourcePageId} vers ${targetPage.id} et sauvegardé:`, draggedElement);
  } catch (error) {
    console.error("❌ Erreur lors du drag interne :", error);
  }
};

// 🔄 Gestionnaires pour Vue Draggable
const handleDragEnd = (event) => {
  console.log("🔄 Vue Draggable - Drag end:", event);
  const { from, to, item } = event;

  // Trouver les pages correspondantes
  const fromPageElement = from.closest('.pageGroup');
  const toPageElement = to.closest('.pageGroup');

  if (fromPageElement && toPageElement && fromPageElement !== toPageElement) {
    // Mise à jour des ordres dans le store
    const fromPageId = fromPageElement.dataset.pageId;
    const toPageId = toPageElement.dataset.pageId;

    if (fromPageId && toPageId) {
      const fromPage = pages.value.find(p => p.id === fromPageId);
      const toPage = pages.value.find(p => p.id === toPageId);

      if (fromPage) store.updatePageOrder(fromPageId, fromPage.children);
      if (toPage) store.updatePageOrder(toPageId, toPage.children);
      
      // 🔄 Sauvegarder les changements en base de données
      store.saveTestDataToDatabase();

      console.log(`✅ Vue Draggable - Déplacement de ${fromPageId} vers ${toPageId} et sauvegardé`);
    }
  }
};

const handleDragAdd = (event) => {
  console.log("🔄 Vue Draggable - Add event:", event);
  // L'élément a été ajouté automatiquement par Vue Draggable
  // Juste s'assurer que le store est à jour
  const targetPageElement = event.to.closest('.pageGroup');
  if (targetPageElement) {
    const pageId = targetPageElement.dataset.pageId;
    const page = pages.value.find(p => p.id === pageId);
    if (page) {
      store.updatePageOrder(pageId, page.children);
      store.saveTestDataToDatabase(); // 🔄 Ajout de la sauvegarde en base
      console.log(`✅ Élément ajouté à la page ${pageId} et sauvegardé`);
    }
  }
};

// 🔄 Gestionnaire pour le début du drag de page
const handlePageDragStart = (event) => {
  console.log("🔄 Page drag start");
  isDraggingPage.value = true;

  // Désactiver temporairement les zones de drop des éléments
  document.body.classList.add('dragging-page');
};

// 🔄 Gestionnaire pour le mouvement du drag de page
const handlePageDragMove = (event) => {
  // Empêcher le drop dans les zones d'éléments
  return !event.related.closest('.collapsed-drop-zone, .drag-container');
};

// 🔄 Gestionnaire pour la réorganisation des pages
const handlePagesReorder = (event) => {
  console.log("🔄 Pages reorder event:", event);

  // Réactiver les zones de drop des éléments
  isDraggingPage.value = false;
  document.body.classList.remove('dragging-page');

  console.log("✅ Ordre des pages mis à jour");
};

// 🔄 Gestionnaire pour l'annulation du drag de page
const handlePageDragCancel = (event) => {
  console.log("🔄 Page drag cancelled");

  // Réactiver les zones de drop des éléments
  isDraggingPage.value = false;
  document.body.classList.remove('dragging-page');
};





</script>


<style scoped>
.position-relative {
  position: relative;
}


.card-title-input {
  width: 100%;
  font-size: 1em;
  font-weight: 430;
}

.pageGroup {
  padding: 10px;
  border: 1px solid grey;
  border-radius: 10px;
}

h3 {
  font-weight: 500;
  font-size: 1.3em;
}

h5 {
  font-weight: 430;
  font-size: 1em;
}

.cursor-pointer {
  cursor: pointer;
}

.pages-container {
  border: 1px gainsboro solid;
  border-radius: 5px;
  margin-left: -7px;
  padding: 10px;
  height: 1600px;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #f9f9f9;
}

.TopPageBT {
  cursor: pointer;
  margin-right: 10px;
  margin-bottom: 20px;
  color: white;
  border: 1px solid rgb(209, 209, 211);
  border-radius: 5px;
}

.TopAdd {
  background-color: #5ba4f7;
}

.TopNode {
  background-color: rgb(78, 212, 78);
}

.TopImport {
  background-color: rgb(83, 37, 86);
}

.action-btn {
  background: none;
  border: none;
  color: #495057;
  cursor: pointer;
  font-size: 16px;
  padding: 0;
  margin-right: 5px;
}

.Page-actions {
  text-align: right;
}

/* Classe pour une page contenant l'élément actif */
.active-page {
  background-color: #d1ecf1;
  border-radius: 12px;
  border: 1px solid #65858a;
  transition: background-color 0.3s, border-color 0.3s;
}

/* Classe pour l'élément actif */
.active-card {
  background-color: #8cb3dd;
  color: white;
  border: 2px solid #335172;
  transition: background-color 0.3s, border-color 0.3s;
}

.delElement {
  margin-right: -10px;
  margin-left: 5px;
  color: rgb(246, 102, 102);
  cursor: pointer;
}

.delElement:hover {
  font-size: 1.2em;
  color: red;
}


.ExportElement {
  margin-left: 5px;
}

.drag-container {
  min-height: 50px;
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 10px;
  background-color: #f9f9f9;
}

.drag-container .card {
  margin-bottom: 10px;
  cursor: grab;
}

.drag-container .card:active {
  cursor: grabbing;
}

.editPageLabel {
  cursor: pointer;
  color: #007bff;
}

.editPageLabel:hover {
  color: #1201fa;
  font-size: 1.2em;
}

.card-body .col-2 {
  opacity: 0;
  /* Masquer les icônes par défaut */
  transition: opacity 0.3s ease;
  /* Animation de l'apparition */
}

.card-body:hover .col-2 {
  opacity: 1;
  /* Afficher les icônes lorsqu'on survole la carte */
}



.icon-container {
  cursor: pointer;
  width: 40px;
  /* Largeur de l'icône */
  height: 40px;
  /* Hauteur de l'icône */
  border-radius: 50%;
  /* Arrondi complet */
  overflow: hidden;
  /* Cache le contenu débordant */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  /* Ombre avec arrondi */
  display: flex;
  justify-content: center;
  align-items: center;
}

.icon-container img {
  width: 100%;
  /* S'adapte à la taille du conteneur */
  height: 100%;

}

.icon-container:hover {
  box-shadow: 0 4px 8px rgba(100, 99, 99, 0.8);
}

.interaction-type {
  padding: 2px 5px 2px 5px;
  width: fit-content;
  background-color: #0DCAF0;
  text-align: center;
  border: 1px solid gainsboro;
  border-radius: 10px;
  margin-top: -10px;
  margin-left: -10px;
  margin-bottom: 10px;
  font-size: 0.8em;
  font-weight: bold;
  color: #f8fafc;
}

.gotoEditor {
  margin-top: -15px;
  margin-right: 10px;
  color: #007bff;
  cursor: pointer;
}

.gotoEditor:hover {
  color: green;
  font-size: 1.2em;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  width: 300px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
}

.addEl {
  font-size: 1.4rem;
  color: #5BA4F7;
  animation: pulse 1.8s ease-out 6;
  border-radius: 10px;
}

.trashcan {
  color: grey;

}


@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0px rgba(0, 123, 255, 0.7);
  }

  70% {
    box-shadow: 0 0 0 5px rgba(0, 123, 255, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(0, 123, 255, 0);
  }
}

/* 🔽 Styles pour la poignée de drag des pages */
.page-drag-handle {
  background: #f8f9fa;
  border: 1px solid #ddd;
  color: #6c757d;
  cursor: grab;
  font-size: 1rem;
  margin-right: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.page-drag-handle:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
  color: #495057;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transform: scale(1.05);
}

.page-drag-handle:active {
  cursor: grabbing;
  transform: scale(0.98);
  background-color: #dee2e6;
}

.page-drag-handle i {
  pointer-events: none;
}

/* 🔽 Container pour les pages draggables */
.pages-draggable-container {
  width: 100%;
}

/* 🔽 Effet visuel pendant le drag de page */
.page-ghost {
  opacity: 0.3;
  background-color: #e3f2fd !important;
  border: 3px dashed #2196f3 !important;
  border-radius: 10px;
  position: relative;
}

.page-ghost::before {
  content: "📄 Drop page here";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #2196f3;
  font-weight: bold;
  font-size: 1.1rem;
  background: white;
  padding: 10px 20px;
  border-radius: 20px;
  border: 2px solid #2196f3;
  z-index: 1000;
}

.page-drag {
  opacity: 0.8;
  transform: rotate(1deg);
  box-shadow: 0 10px 30px rgba(33, 150, 243, 0.3);
  border: 2px solid #2196f3;
  background-color: rgba(227, 242, 253, 0.9);
}

.page-chosen {
  border: 2px solid #2196f3;
  background-color: #e3f2fd;
}

/* 🔽 Désactiver les zones vertes quand on drag une page */
.dragging-page .collapsed-drop-zone,
.dragging-page .drag-container {
  pointer-events: none;
  opacity: 0.3;
  border-color: #ccc !important;
  background-color: #f5f5f5 !important;
}

.dragging-page .collapsed-drop-zone.drag-over,
.dragging-page .drag-container.drag-over {
  border-color: #ccc !important;
  background-color: #f5f5f5 !important;
  transform: none !important;
  box-shadow: none !important;
}

/* 🔽 Styles pour le système de collapse */
.collapse-caret {
  background: #f8f9fa;
  border: 1px solid #ddd;
  color: #495057;
  cursor: pointer;
  font-size: 1rem;
  margin-right: 12px;
  padding: 6px 8px;
  border-radius: 6px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.collapse-caret:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
  color: #212529;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transform: scale(1.1);
}

.collapse-caret:active {
  transform: scale(0.95);
}

.caret-icon {
  display: inline-block;
  transition: transform 0.3s ease;
  font-size: 0.9rem;
  line-height: 1;
}

.collapse-caret.collapsed .caret-icon {
  transform: rotate(-90deg);
}

.page-title {
  margin: 0;
  user-select: none;
}

.page-header {
  margin-bottom: 10px;
}

/* 🔽 Styles pour le contenu de page repliable */
.page-content {
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
}

.page-content.collapsed {
  max-height: 125px;
  /* Optimisé pour l'aperçu + zone de drop */
}

/* 🔽 Effet de survol lors du drag pour toute la page */
.page-content:has(.drag-over) {
  background-color: rgba(40, 167, 69, 0.1);
  border-radius: 8px;
}

/* 🔽 Container pour le contenu repliable */
.collapsed-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* 🔽 Message pour pages vides */
.empty-page-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px;
  height: 60px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px dashed #dee2e6;
}

/* 🔽 Styles pour l'aperçu empilé */
.stacked-preview {
  position: relative;
  height: 60px;
  margin: 0;
  display: flex;
  align-items: flex-start;
}

.stacked-card {
  position: absolute;
  left: 0;
  top: 0;
  transition: all 0.3s ease;
}

.card-mini {
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 8px 12px;
  width: 200px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
}

.card-mini:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.interaction-type-mini {
  font-size: 0.7rem;
  font-weight: bold;
  color: #0DCAF0;
  margin-bottom: 4px;
}

.card-title-mini {
  font-size: 0.85rem;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.more-items {
  position: absolute;
  left: 210px;
  top: 20px;
  font-size: 0.8rem;
  color: #666;
  font-style: italic;
}

/* 🔽 Zone de drop pour pages repliées */
.collapsed-drop-zone {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 10px;
  margin: 0;
  background-color: #f9f9f9;
  text-align: center;
  transition: all 0.3s ease;
  min-height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 10;
}

.collapsed-drop-zone:hover {
  border-color: #5ba4f7;
  background-color: #f0f8ff;
}

.collapsed-drop-zone.drag-over {
  border-color: #28a745;
  background-color: #d4edda;
  border-style: solid;
  transform: scale(1.02);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.drop-indicator {
  color: #666;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-direction: column;
}

.drop-indicator span {
  font-weight: 500;
}

.collapsed-drop-zone:hover .drop-indicator {
  color: #5ba4f7;
}

.collapsed-drop-zone.drag-over .drop-indicator {
  color: #28a745;
  font-weight: bold;
}

/* 🔽 Draggable invisible pour capturer les drops */
.invisible-draggable {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  background: transparent;
  min-height: 100%;
}

/* 🔽 Animation pour le repliement */
.pageGroup.collapsed {
  transition: all 0.3s ease;
}

.collapsed-page {
  margin-bottom: 15px;
}

/* 🔽 Ajustements pour les pages repliées */
.collapsed-page .pageGroup {
  padding-bottom: 15px;
}

/* 🔽 Effet visuel quand on drag au-dessus d'une page */
.page-drag-over .pageGroup {
  background-color: rgba(40, 167, 69, 0.1);
  border-color: #28a745;
  box-shadow: 0 0 10px rgba(40, 167, 69, 0.3);
  transform: scale(1.01);
}

/* 🔽 Effet hover sur les cartes empilées */
.stacked-preview:hover .stacked-card {
  transform: translateY(0px) translateX(0px) !important;
  opacity: 1 !important;
}

.stacked-preview:hover .stacked-card:nth-child(1) {
  left: 0px;
}

.stacked-preview:hover .stacked-card:nth-child(2) {
  left: 210px;
}

.stacked-preview:hover .stacked-card:nth-child(3) {
  left: 420px;
}


.pageParameter {
  background-color: whitesmoke;
  font-size: 15px;
  color: grey;
  border: 1px gainsboro solid;
  border-radius: 5px;
}

.pageParameter:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
  color: #212529;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transform: scale(1.1);
}



.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.left-group,
.center-group,
.right-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.center-group {
  flex: 1;
  justify-content: center;
}

/* Pour les petits écrans */
@media (max-width: 768px) {
  .header-content {
    flex-wrap: wrap;
    gap: 10px;
  }

  .center-group {
    order: 3;
    flex-basis: 100%;
    justify-content: flex-start;
  }
}

/* Permet le scroll vertical et le tap fiable sur carte */
.card { 
  touch-action: pan-y; 
  user-select: none; /* Empêche la sélection de texte */
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* Poignée de drag fine sur toute la largeur */
.drag-handle { 
  touch-action: manipulation; 
  cursor: grab; 
  height: 8px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  pointer-events: auto;
  border-radius: 8px 8px 0 0;
  background: linear-gradient(to right, transparent 0%, #e9ecef 20%, #e9ecef 80%, transparent 100%);
  position: relative;
  transition: all 0.2s ease;
}

/* Indicateur visuel central */
.drag-indicator {
  width: 40px;
  height: 3px;
  background-color: #adb5bd;
  border-radius: 2px;
  opacity: 0.7;
  transition: all 0.2s ease;
}

/* Masquer l'icône grip verticale disgracieuse */
.drag-handle .fas.fa-grip-vertical {
  display: none;
}

.drag-handle:hover {
  background: linear-gradient(to right, transparent 0%, #dee2e6 15%, #dee2e6 85%, transparent 100%);
}

.drag-handle:hover .drag-indicator {
  background-color: #6c757d;
  width: 60px;
  opacity: 1;
}

.drag-handle:active {
  cursor: grabbing;
  background: linear-gradient(to right, transparent 0%, #ced4da 10%, #ced4da 90%, transparent 100%);
}

.drag-handle:active .drag-indicator {
  background-color: #495057;
  width: 50px;
}

/* Zone de contenu cliquable */
.card-content {
  cursor: pointer;
  flex: 1;
}

/* Zone cliquable vs zone draggable */
.card-body {
  position: relative;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* Amélioration du feedback visuel */
.card.cursor-pointer:active {
  transform: scale(0.98);
  transition: transform 0.1s ease;
}

</style>
